import { useState, useMemo, useEffect, useCallback } from "react";
import { useAuthStore } from "@/store/authStore";
import API from "@/lib/API";

export const useGeneralExpensesLogic = (initialCategory = "all") => {
  const { user } = useAuthStore();
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: initialCategory, dateRange: { from: "", to: "" } });
  const [sortBy, setSortBy] = useState("newest");
  const [showStats, setShowStats] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const role = user?.role || "";
  const isOwnerOrAdmin = ["owner", "admin", "verve-admin", "Verve-admin"].includes(role);
  const isFinanceManager = ["finance-manager", "accountant"].includes(role);
  const isPMOrSales = ['designer', "sales-head", 'designer', 'store_incharge'].includes(role);
  const canManageExpenses = isOwnerOrAdmin || isFinanceManager || isPMOrSales;
  const canDisburseExpenses = isOwnerOrAdmin || isFinanceManager; // Only owner or accountant can disburse

  const fetchGeneralExpenses = useCallback(async () => {
    try {
      const res = await API.get("/general-expenses");
      setExpenses(res.data?.data || res.data || res || []);
    } catch (err) {
      console.error("Failed to fetch general expenses:", err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await fetchGeneralExpenses();
      const results = await Promise.allSettled([
        API.get("/users?limit=1000"),
        API.get("/general-expenses/projects") // To get projects/accounts for expenses
      ]);

      const empParams = results[0];
      if (empParams.status === "fulfilled") {
        const empRes = empParams.value;
        const rawEmployees = empRes?.users || empRes?.data?.users || empRes?.data || [];
        if (Array.isArray(rawEmployees)) {
          const processed = rawEmployees.map(e => ({
            ...e,
            displayName: e.name || `${e.firstName || ""} ${e.lastName || ""}`.trim() || e.email || "Staff Member"
          }));
          setEmployees(processed);
        }
      }

      const projParams = results[1];
      if (projParams.status === "fulfilled") {
        const projRes = projParams.value;
        const rawProjects = projRes?.accounts || projRes?.projects || projRes?.data?.accounts || projRes?.data || [];
        console.log("Fetched Projects for dropdown:", rawProjects);
        if (Array.isArray(rawProjects)) {
          setProjects(rawProjects);
        }
      } else {
        console.error("Failed to fetch projects for dropdown:", projParams.reason);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchGeneralExpenses]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddExpense = async (data) => {
    try {
      await API.post("/general-expenses", data);
      await fetchGeneralExpenses();
      return true;
    } catch (err) {
      console.error("Failed to add expense:", err);
      throw err;
    }
  };

  const handleEditExpense = async (id, data) => {
    try {
      await API.patch(`/general-expenses/${id}`, data);
      await fetchGeneralExpenses();
      return true;
    } catch (err) {
      console.error("Failed to edit expense:", err);
      throw err;
    }
  };

  const handleToggleDisable = async (id, isCurrentlyDisabled) => {
    try {
      await API.patch(`/general-expenses/${id}`, { status: isCurrentlyDisabled ? 'pending' : 'cancelled' });
      await fetchGeneralExpenses();
      return true;
    } catch (err) {
      console.error("Failed to toggle expense status:", err);
      throw err;
    }
  };

  const handleDisburseExpense = async (id, data) => {
    try {
      await API.patch(`/general-expenses/${id}`, { status: 'paid', ...data });
      await fetchGeneralExpenses();
      return true;
    } catch (err) {
      console.error("Failed to disburse expense:", err);
      throw err;
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses
      .filter(p => {
        const matchesSearch =
          (p.reason || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.toWhomPaid || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.expenseId || "").toLowerCase().includes(search.toLowerCase());

        // Status Filter
        const matchesStatus = !filters.status || filters.status === 'all' ||
          (p.status || '').toLowerCase() === filters.status.toLowerCase();

        // Category Logic: 'general' means NOT related to project, 'project' means related to project
        const isProjectExp = p.relatedToProject === true || String(p.relatedToProject).toLowerCase() === 'yes';
        let matchesCategory = true;
        if (filters.category === "general") {
          matchesCategory = !isProjectExp;
        } else if (filters.category === "project") {
          matchesCategory = isProjectExp;
        }

        // Date Range Filtering
        const expDate = new Date(p.date || p.createdAt);
        const getMidnight = (d) => { const dt = new Date(d); dt.setHours(0, 0, 0, 0); return dt.getTime(); };
        const expTime = getMidnight(expDate);

        const matchesFromDate = !filters.fromDate || expTime >= getMidnight(filters.fromDate);
        const matchesToDate = !filters.toDate || expTime <= getMidnight(filters.toDate);

        // Quick Date Toggles
        let matchesQuickDate = true;
        if (filters.date && filters.date !== 'all') {
          const now = new Date();
          const today = getMidnight(now);

          if (filters.date === 'today') {
            matchesQuickDate = expTime === today;
          } else if (filters.date === 'this_week') {
            const temp = new Date(now);
            const startOfWeek = getMidnight(temp.setDate(temp.getDate() - temp.getDay()));
            matchesQuickDate = expTime >= startOfWeek;
          } else if (filters.date === 'this_month') {
            matchesQuickDate = expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear();
          } else if (filters.date === 'this_year') {
            matchesQuickDate = expDate.getFullYear() === now.getFullYear();
          }
        }

        return matchesSearch && matchesStatus && matchesCategory && matchesFromDate && matchesToDate && matchesQuickDate;
      })
      .map(e => {
        const project = projects.find(p => p._id === e.relatedProjectId || p.projectId === e.relatedProjectId);
        return {
          ...e,
          displayProjectId: project?.customerCustomId || e.projectDetails?.projectId || project?.projectId || e.relatedProjectId,
          projectCustomerName: e.projectDetails?.customerName || project?.customerName || (e.relatedToProject ? 'N/A' : '—'),
          projectLocation: e.projectDetails?.location || project?.location || (e.relatedToProject ? 'Unknown' : '—')
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        if (sortBy === "newest") return dateB - dateA;
        if (sortBy === "oldest") return dateA - dateB;
        if (sortBy === "amount_h_l") return Math.abs(b.amount) - Math.abs(a.amount);
        return 0;
      });
  }, [expenses, search, filters, sortBy]);

  const stats = useMemo(() => {
    // For stats: { ...stats, category: initialCategory }, we use approvedAmount if it exists (for finalized entries), otherwise fallback to the requested amount
    const getFinalAmount = (p) => Math.abs(p.approvedAmount || p.amount || 0);

    const requestedTotal = filteredExpenses.reduce((s, p) => s + Math.abs(p.amount || 0), 0);
    const total = filteredExpenses.reduce((s, p) => s + getFinalAmount(p), 0);

    const approvedAmount = filteredExpenses
      .filter(p => ['approved', 'partially_approved', 'owner_approved'].includes(p.status))
      .reduce((s, p) => s + (p.approvedAmount || p.amount || 0), 0);

    const projectExpensesTotal = filteredExpenses
      .filter(p => p.relatedToProject === true || String(p.relatedToProject).toLowerCase() === 'yes')
      .reduce((s, p) => s + getFinalAmount(p), 0);

    const otherExpensesTotal = filteredExpenses
      .filter(p => !(p.relatedToProject === true || String(p.relatedToProject).toLowerCase() === 'yes'))
      .reduce((s, p) => s + getFinalAmount(p), 0);

    const monthlyDataObj = filteredExpenses.reduce((acc, exp) => {
      const d = new Date(exp.date);
      if (isNaN(d.getTime())) return acc;
      const month = d.toLocaleString('default', { month: 'short' });
      acc[month] = (acc[month] || 0) + Math.abs(exp.amount);
      return acc;
    }, {});

    const pendingPaymentsCount = filteredExpenses
      .filter(p => ['approved', 'partially_approved', 'owner_approved'].includes((p.status || '').toLowerCase())).length;

    return {
      count: filteredExpenses.length,
      requestedTotal,
      approvedAmount,
      otherExpensesTotal,
      projectExpensesTotal,
      pendingPaymentsCount,
      trendData: Object.entries(monthlyDataObj).map(([name, value]) => ({ name, value })).slice(-6)
    };
  }, [filteredExpenses]);

  const totalPages = Math.ceil(filteredExpenses.length / pageSize);
  const paginatedExpenses = filteredExpenses.slice((page - 1) * pageSize, page * pageSize);

  return {
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    view, setView,
    showStats, setShowStats,
    isModalOpen, setIsModalOpen,
    page, setPage, totalPages,
    expenses: paginatedExpenses,
    employees,
    projects,
    totalCount: filteredExpenses.length,
    stats: { ...stats, category: initialCategory },
    user,
    loading,
    handleAddExpense,
    handleEditExpense,
    handleToggleDisable,
    handleDisburseExpense,
    canManageExpenses,
    canDisburseExpenses
  };
};
